import { Input } from "@headlessui/react";
import SearchIcon from "@/assets/icons/search-lg.svg?react";
import "rsuite/DateRangePicker/styles/index.css";
import { InvoiceStatusSelect } from "../invoiceStatusSelect";
import { StyledYearSelect } from "@/components/styled/StyledYearSelect";
import { StyledMonthSelect } from "@/components/styled/StyledMonthSelect";
import { RibReducedDto } from "@/types/api/api-types";
import GenericSelect from "@/components/ui/customs/generic-select";
import { ChartNoAxesCombined, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CustomInput } from "@/components/ui/customs/input";
import { useTranslation } from "react-i18next";

type InvoicesFilterProps = {
  setSearchField: React.Dispatch<React.SetStateAction<string>>;
  searchField: string;
  setStatusField: React.Dispatch<React.SetStateAction<string>>;
  statusField: string;
  setYear: React.Dispatch<React.SetStateAction<number | undefined>>;
  setMonth: React.Dispatch<React.SetStateAction<number[] | undefined>>;
  initMonth?: boolean;
  setRibId: React.Dispatch<React.SetStateAction<string | undefined>>;
  ribId: string | undefined;
  ribs: RibReducedDto[];
  setDisplayStats: React.Dispatch<React.SetStateAction<boolean>>;
  setDisplayDownloadPdf: React.Dispatch<React.SetStateAction<boolean>>;
  setAmountToBill: React.Dispatch<React.SetStateAction<(number | undefined)[]>>;
  amountToBill: (number | undefined)[];
  displayStats: boolean;
  displayDownloadPdf: boolean;
  hasCompanyId: boolean;
};

export const InvoicesFilter = ({
  setSearchField,
  searchField,
  setStatusField,
  statusField,
  setYear,
  setMonth,
  initMonth = true,
  setRibId,
  ribId,
  ribs,
  setDisplayStats,
  setDisplayDownloadPdf,
  displayStats,
  displayDownloadPdf,
  hasCompanyId,
  setAmountToBill,
  amountToBill,
}: InvoicesFilterProps) => {
  const { t } = useTranslation("boInvoices");
  const defaultYear =
    new Date().getMonth() === 0 ? new Date().getFullYear() - 1 : new Date().getFullYear();
  const defaultMonth = new Date().getMonth() === 0 ? 11 : new Date().getMonth() - 1;

  return (
    <div className="flex w-full flex-col justify-start gap-2">
      <div className="flex flex-col items-start justify-between gap-2 lg:flex-row">
        <InvoiceStatusSelect setStatusField={setStatusField} statusField={statusField} />
        {!hasCompanyId && (
          <div className="flex flex-row items-center gap-2">
            <div className="flex items-center gap-2">
              {!displayStats && (
                <Button
                  onClick={() => setDisplayDownloadPdf(!displayDownloadPdf)}
                  variant="outline"
                >
                  {t("invoicesFilter.downloadInvoices")}
                  <FileDown />
                </Button>
              )}
              <Button
                onClick={() => setDisplayStats(!displayStats)}
                variant="outline"
                className={`${displayStats ? "bg-gray-200" : "bg-white"}`}
              >
                <ChartNoAxesCombined />
              </Button>
            </div>
          </div>
        )}
      </div>
      <div className="flex max-w-[300px] flex-col items-start gap-2 lg:max-w-screen lg:flex-row lg:items-center">
        <div className="flex h-[46px] w-full min-w-[150px] flex-row items-center gap-3 rounded-lg border border-solid border-gray-300 bg-white px-3 py-2 lg:max-w-[268px]">
          <SearchIcon />
          <Input
            value={searchField}
            onChange={(e) => setSearchField(e.target.value)}
            className="w-full text-sm font-medium focus:outline-hidden"
            placeholder={t("invoicesFilter.searchPlaceholder")}
          />
        </div>
        {displayStats ? (
          <>
            <StyledYearSelect
              onChange={setYear}
              isSearchable={false}
              isClearable={false}
              defaultValue={defaultYear}
              className="w-full lg:max-w-[105px] lg:min-w-[105px]"
            />
            <StyledMonthSelect
              onChange={setMonth}
              isSearchable={false}
              defaultValue={initMonth ? defaultMonth : undefined}
              className="w-full lg:max-w-[220px] lg:min-w-[105px]"
              isMulti={true}
            />
          </>
        ) : (
          <>
            <StyledYearSelect
              onChange={setYear}
              isSearchable={false}
              isClearable={false}
              defaultValue={defaultYear}
              className="w-full lg:max-w-[105px] lg:min-w-[105px]"
            />
            <StyledMonthSelect
              onChange={setMonth}
              isSearchable={false}
              defaultValue={initMonth ? defaultMonth : undefined}
              className="w-full lg:max-w-[220px] lg:min-w-[105px]"
            />
          </>
        )}
        {!hasCompanyId && (
          <>
            {!displayStats && (
              <GenericSelect
                value={ribId}
                onChange={setRibId}
                placeholder={t("invoicesFilter.ribPlaceholder")}
                options={[
                  ...ribs.map((rib) => ({
                    label: rib.shortenName,
                    value: rib.id as string,
                  })),
                ]}
                className="min-h-[48px] w-[300px] lg:max-w-[150px] lg:min-w-[150px]"
                clearable={true}
              />
            )}
            <CustomInput
              value={amountToBill[0]}
              onChange={(e) => setAmountToBill([Number(e), amountToBill[1]])}
              placeholder={t("invoicesFilter.minAmountPlaceholder")}
              className="min-h-[48px] bg-white lg:max-w-[120px]"
              inputType="float"
            />
            <CustomInput
              value={amountToBill[1]}
              onChange={(e) => setAmountToBill([amountToBill[0], Number(e)])}
              placeholder={t("invoicesFilter.maxAmountPlaceholder")}
              className="min-h-[48px] bg-white lg:max-w-[120px]"
              inputType="float"
            />
          </>
        )}
      </div>
    </div>
  );
};
